import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Link from "@insite/mobius/Link";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth",
    nodeId = "nodeId",
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
    };
}

const includeField = ["Accessorials", "Terms", "Security Roles", "Add Security Role", "Edit Security Role"];
const includeRole = ["Administrator"];

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    fieldstoHide: state.context.settings.properties.fieldsToHide || "",
    canshowPrice: state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
    featurePages: state.context.session.properties?.featurePages?.split(",").map(page => page.trim()) || [],
    ootbFeaturePages: state?.context?.session?.properties?.ootbFeaturePages?.split(",").map(page => page.trim()) || [],
    userSession: state.context.session,
    settings: state.context.settings,
    currentBillTo: getBillToState(state, getSession(state).billToId).value,
    isBuyer3: state.context.session?.userRoles?.toLowerCase()?.includes("buyer3") || false,
});

interface ListStyleProp {
    leftMargin: number;
    marginBottom: number;
}

const ListStyle = styled.ul<ListStyleProp>`
    margin-left: ${props => props.leftMargin}px;
    li {
        margin-bottom: ${props => props.marginBottom}px;
    }
`;

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const NavigationList = (props: Props) => {
    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    const renderLink = (pageLink: PageLinkModel, currentDepth: number) => {
        if (currentDepth > props.fields.depth) {
            return;
        }

        if (
            pageLink.excludeFromNavigation ||
            (pageLink.type === "ChangeCustomerPage" && !props.displayChangeCustomerLink)
        ) {
            return;
        }

        if (pageLink.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;

        return (
            <li key={pageLink.id} className="navigation-list-item">
                <Link
                    className="navigation-list-link"
                    data-test-selector={`navigationList-${pageLink.title}`}
                    href={pageLink.url}
                >
                    {pageLink.title}
                </Link>
                {pageLink.children && pageLink.children.length > 0 && (
                    <ListStyle
                        marginBottom={props.fields.marginBottom}
                        leftMargin={props.fields.leftMargin}
                        key={pageLink.id}
                    >
                        {pageLink.children.map(link => renderLink(link, nextDepth))}
                    </ListStyle>
                )}
            </li>
        );
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <ListStyle
                className="navigation-list"
                marginBottom={props.fields.marginBottom}
                leftMargin={props.fields.leftMargin}
                data-test-selector="navigationList"
            >
                {props.rootPageLink.title === "MyAccount"
                    ? props.rootPageLink.children
                          ?.filter(child => {
                              let flag = true;
                              includeRole?.forEach((role: string) => {
                                  if (includeField?.includes(child.title)) {
                                      if (
                                          props.userSession?.userRoles?.indexOf(role) !== -1 ||
                                          (child.title === "Terms" && props.userSession?.isSalesPerson)
                                      ) {
                                          flag = true;
                                      } else {
                                          flag = false;
                                      }
                                  }
                              });
                              if (child.title === "Reports") {
                                  flag = !(
                                      props.settings?.properties?.backOrderedItemsReportsType === "OFF" &&
                                      props.settings?.properties?.purchaseHistoryReportsType === "OFF"
                                  );
                              }
                              return flag;
                          })
                          ?.filter(child => {
                              const title = child?.title?.trim();

                              // Safely split and normalize excluded titles
                              const excludedTitles =
                                  !props.canshowPrice && props.fieldstoHide
                                      ? props.fieldstoHide.split(",").map(t => t.trim().toLowerCase())
                                      : [];

                              if (!title) {
                                  return true;
                              }

                              if (props.featurePages.includes(title)) {
                                  return false;
                              }
                              if (props.ootbFeaturePages.includes(title)) {
                                  return false;
                              }

                              if (title === "Requisitions" && props.isBuyer3) {
                                  return false;
                              }

                              if (
                                  title === "Requisitions" &&
                                  props.currentBillTo?.properties?.disableRequisitionCheckout?.toLowerCase() === "true"
                              ) {
                                  return false;
                              }
                              return !excludedTitles.includes(title.toLowerCase());
                          })
                          .map(link => renderLink(link, 1))
                    : props.rootPageLink.children.map(link => renderLink(link, 1))}
            </ListStyle>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavigationList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
